/*
 * MMX Collection Grid — <mmx-collection-grid>, rendered by sections/mmx-collection-grid.liquid.
 *
 * - Filter pills show all items, only bundle cards or only product ("single") cards.
 * - "See more" reveals the next page of the current filter; changing the filter goes back to the first page.
 *
 * Changes are animated FLIP-style: leaving cards fade out, cards that stay glide to their new grid cell, new cards
 * rise in with a stagger and the grid height follows. With reduced motion the grid just updates.
 */
(() => {
  if (window.customElements.get('mmx-collection-grid')) {
    return;
  }

  const OUT_MS = 220;
  const MOVE_MS = 520;
  const IN_MS = 520;
  const STAGGER_MS = 70;
  const MAX_STAGGER = 8;
  const EASE_OUT = 'cubic-bezier(0.22, 1, 0.36, 1)';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class MMXCollectionGrid extends HTMLElement {
    #abortController = null;
    #animations = new Set();
    #filter = 'all';
    #limit = 0;
    #pageSize = 6;
    #runId = 0;

    connectedCallback() {
      this.#abortController = new AbortController();
      const { signal } = this.#abortController;

      this.grid = this.querySelector('[data-grid]');
      this.seeMore = this.querySelector('[data-see-more]');
      this.#pageSize = Math.max(1, parseInt(this.dataset.pageSize, 10) || 6);
      this.#limit = this.#pageSize;

      if (!this.grid) {
        return;
      }

      this.addEventListener('click', (event) => {
        const target = event.target instanceof Element ? event.target : null;
        const pill = target?.closest('[data-filter]');

        if (pill) {
          this.#setFilter(pill);
        } else if (target?.closest('[data-see-more]')) {
          this.#showMore();
        }
      }, { signal });

      // Blocks added, removed or re-ordered in the theme editor re-render the section; keep a selected block in view
      if (window.Shopify?.designMode) {
        this.addEventListener('shopify:block:select', (event) => this.#revealBlock(event.target), { signal });
      }

      this.#update({ animate: false });
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.#runId += 1;
      this.#cancelAnimations();
    }

    get #items() {
      return Array.from(this.grid.querySelectorAll('[data-grid-item]'));
    }

    #matches(item) {
      return this.#filter === 'all' || item.dataset.type === this.#filter;
    }

    #setFilter(pill, { animate = true } = {}) {
      const filter = pill?.dataset.filter;

      if (!filter || filter === this.#filter) {
        return;
      }

      this.#filter = filter;
      this.#limit = this.#pageSize;

      this.querySelectorAll('[data-filter]').forEach((element) => {
        const isActive = element === pill;
        element.classList.toggle('is-active', isActive);
        element.setAttribute('aria-pressed', String(isActive));
      });

      if (animate) {
        this.#update();
      }
    }

    #showMore() {
      const wasFocused = document.activeElement === this.seeMore;
      this.#limit += this.#pageSize;

      const entering = this.#update();

      // The button disappears on the last page; move keyboard focus to the first card it revealed
      if (wasFocused && this.seeMore?.hidden && entering[0]) {
        entering[0].querySelector('a[href], button:not([disabled])')?.focus({ preventScroll: true });
      }
    }

    #revealBlock(element) {
      const item = element?.closest?.('[data-grid-item]');

      if (!item || !item.hidden) {
        return;
      }

      if (!this.#matches(item)) {
        this.#setFilter(this.querySelector('[data-filter="all"]'), { animate: false });
      }

      const index = this.#items.filter((candidate) => this.#matches(candidate)).indexOf(item);
      this.#limit = Math.max(this.#limit, Math.ceil((index + 1) / this.#pageSize) * this.#pageSize);
      this.#update({ animate: false });
    }

    // Applies the filter and limit; returns the items that became visible
    #update({ animate = true } = {}) {
      const items = this.#items;
      const matching = items.filter((item) => this.#matches(item));
      const visible = new Set(matching.slice(0, this.#limit));
      const entering = items.filter((item) => item.hidden && visible.has(item));

      if (this.seeMore) {
        this.seeMore.hidden = matching.length <= this.#limit;
      }

      // Any running transition is superseded: stop it and invalidate its pending steps
      this.#runId += 1;
      this.#cancelAnimations();

      // Hidden tabs don't advance animations, so their steps would never run
      if (!animate || reducedMotion.matches || document.hidden || typeof this.grid.animate !== 'function') {
        items.forEach((item) => {
          item.hidden = !visible.has(item);
        });
        return entering;
      }

      this.#animate(items, visible, entering);
      return entering;
    }

    async #animate(items, visible, entering) {
      const runId = this.#runId;

      const leaving = items.filter((item) => !item.hidden && !visible.has(item));
      const staying = items.filter((item) => !item.hidden && visible.has(item));
      const startHeight = this.grid.offsetHeight;

      // 1. Fade out the cards that no longer match
      let fadeOuts = [];

      if (leaving.length > 0) {
        fadeOuts = leaving.map((item) => this.#run(item, [
          { opacity: 1, transform: 'none' },
          { opacity: 0, transform: 'translateY(12px) scale(0.96)' },
        ], { duration: OUT_MS, easing: 'ease-in', fill: 'forwards' }));

        await Promise.all(fadeOuts.map(({ finished }) => finished));

        if (runId !== this.#runId) {
          return;
        }
      }

      // 2. Measure, swap the visible set, measure again
      const firstRects = new Map(staying.map((item) => [item, item.getBoundingClientRect()]));
      const heightBefore = leaving.length > 0 ? this.grid.offsetHeight : startHeight;

      items.forEach((item) => {
        item.hidden = !visible.has(item);
      });
      fadeOuts.forEach(({ animation }) => animation.cancel());

      const heightAfter = this.grid.offsetHeight;

      // 3. Glide the cards that stayed from their old cell to the new one
      staying.forEach((item) => {
        const first = firstRects.get(item);
        const last = item.getBoundingClientRect();
        const dx = first.left - last.left;
        const dy = first.top - last.top;

        if (Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5) {
          this.#run(item, [
            { transform: `translate(${dx}px, ${dy}px)` },
            { transform: 'none' },
          ], { duration: MOVE_MS, easing: EASE_OUT });
        }
      });

      // 4. New cards rise in, one after another
      entering.forEach((item, index) => {
        this.#run(item, [
          { opacity: 0, transform: 'translateY(40px) scale(0.97)' },
          { opacity: 1, transform: 'none' },
        ], { duration: IN_MS, delay: Math.min(index, MAX_STAGGER) * STAGGER_MS, easing: EASE_OUT, fill: 'backwards' });
      });

      // 5. The grid height follows, so the content below slides instead of jumping
      if (Math.abs(heightBefore - heightAfter) > 1) {
        this.grid.style.overflow = 'clip';
        this.#run(this.grid, [
          { height: `${heightBefore}px` },
          { height: `${heightAfter}px` },
        ], { duration: MOVE_MS, easing: EASE_OUT }).finished.then(() => {
          if (runId === this.#runId) {
            this.grid.style.overflow = '';
          }
        });
      }
    }

    // Returns the animation and a promise that settles when it ends or is cancelled
    #run(element, keyframes, options) {
      const animation = element.animate(keyframes, options);
      this.#animations.add(animation);

      const finished = animation.finished
        .catch(() => {})
        .finally(() => this.#animations.delete(animation));

      return { animation, finished };
    }

    #cancelAnimations() {
      this.#animations.forEach((animation) => animation.cancel());
      this.#animations.clear();

      if (this.grid) {
        this.grid.style.overflow = '';
      }
    }
  }

  window.customElements.define('mmx-collection-grid', MMXCollectionGrid);
})();
